import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { HERO_STATS, MIRRORED, PHONES_IMG, HERO_TRUST } from "../mock/data";
import CountUp from "./CountUp";
import api from "../lib/api";

export default function Hero() {
  const [stats, setStats] = useState(HERO_STATS);
  const zoneRef = useRef(null);
  const phoneRef = useRef(null);
  const card1Ref = useRef(null);
  const card2Ref = useRef(null);
  const glowRef = useRef(null);
  const frame = useRef(0);

  useEffect(() => {
    api.get("/track-record")
      .then((r) => { if (r.data?.hero_stats?.length) setStats(r.data.hero_stats); })
      .catch(() => {});
  }, []);

  const handleMove = (e) => {
    const el = zoneRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;   // -0.5 .. 0.5
    const py = (e.clientY - r.top) / r.height - 0.5;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      if (phoneRef.current)
        phoneRef.current.style.transform =
          `rotateY(${px * 14}deg) rotateX(${-py * 12}deg) translate3d(${px * 22}px, ${py * 16}px, 0)`;
      if (card1Ref.current)
        card1Ref.current.style.transform = `translate3d(${px * 46}px, ${py * 36}px, 0)`;
      if (card2Ref.current)
        card2Ref.current.style.transform = `translate3d(${px * -50}px, ${py * -38}px, 0)`;
      if (glowRef.current)
        glowRef.current.style.transform = `translate3d(${px * 40}px, ${py * 40}px, 0)`;
    });
  };

  const handleLeave = () => {
    cancelAnimationFrame(frame.current);
    [phoneRef, card1Ref, card2Ref, glowRef].forEach((r) => {
      if (r.current) r.current.style.transform = "";
    });
  };

  return (
    <section id="top" className="relative grid-bg pt-[68px] overflow-hidden">
      {/* radial glow */}
      <div ref={glowRef} className="pointer-events-none absolute -top-40 right-0 w-[700px] h-[700px] rounded-full transition-transform duration-300 ease-out will-change-transform"
        style={{ background: "radial-gradient(circle, rgba(196,245,66,0.08) 0%, transparent 65%)" }} />

      <div className="max-w-[1300px] mx-auto px-5 md:px-8 pt-14 pb-16 grid lg:grid-cols-2 gap-10 items-center relative">
        {/* Left */}
        <div>
          <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-3.5 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span className="label text-[var(--muted)]">Sonic AI · XAU/USD · Live since November 2024</span>
          </div>

          <h1 className="display text-[clamp(2.6rem,6.2vw,5.2rem)]">
            A professional gold strategy,{" "}
            <span className="text-accent">copied to your</span> own account.
          </h1>

          <p className="mt-7 max-w-xl text-[var(--muted)] text-[15px] leading-relaxed">
            Sonic AI trades one market — gold against the dollar. CopyX mirrors every entry and exit onto
            a TAG Markets account held in your own name, on a 12× or 24× amplified structure. You keep
            control of your capital; the desk does the trading.
          </p>

          <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-xl">
            {HERO_TRUST.map((t, i) => (
              <li key={i} className="flex items-center gap-2.5 text-[13px] text-[var(--text)]">
                <span className="w-4 h-4 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                  <Check size={11} className="text-accent" strokeWidth={2.6} />
                </span>
                {t}
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#join" className="btn-accent px-6 py-3.5 rounded-lg inline-flex items-center gap-2">
              Join the community <ArrowRight size={14} />
            </a>
            <a href="#calculator" className="btn-ghost px-6 py-3.5 rounded-lg inline-flex items-center gap-2">
              Open the ROI calculator <ArrowRight size={14} />
            </a>
          </div>
        </div>

        {/* Right: phones */}
        <div ref={zoneRef} onMouseMove={handleMove} onMouseLeave={handleLeave}
          className="relative" style={{ perspective: "1200px" }}>
          {/* parallax phone layer (mouse) wrapping the floaty bob layer */}
          <div ref={phoneRef} className="relative z-10 transition-transform duration-200 ease-out will-change-transform"
            style={{ transformStyle: "preserve-3d" }}>
            <div className="floaty">
              <img src={PHONES_IMG} alt="The CopyX app showing the Sonic AI strategy and its live performance"
                className="w-full max-w-[430px] mx-auto drop-shadow-2xl" loading="eager" />
            </div>
          </div>

          {/* floating stat card */}
          <div ref={card1Ref} className="hidden lg:block absolute top-2 -left-4 xl:-left-10 z-20 transition-transform duration-200 ease-out will-change-transform">
            <div className="floaty-soft card rounded-2xl px-4 py-3 backdrop-blur-md bg-[#0d110c]/90">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="label text-[var(--muted)] text-[0.6rem]">Live since November 2024</span>
              </div>
              <div className="display text-accent text-2xl">+29.23%</div>
              <div className="text-[11px] text-[var(--muted)]">Total gain, account level</div>
            </div>
          </div>

          {/* floating mirrored activity */}
          <div ref={card2Ref} className="hidden lg:block absolute bottom-4 -right-4 xl:-right-10 z-20 transition-transform duration-200 ease-out will-change-transform">
            <div className="floaty-slow card rounded-2xl px-4 py-3 backdrop-blur-md bg-[#0d110c]/90 w-[200px]">
              <div className="label text-[var(--muted)] text-[0.6rem] mb-2">Mirrored activity</div>
              {MIRRORED.map((m, i) => (
                <div key={i} className="flex items-center justify-between text-[12px] py-1 mono">
                  <span className="text-[var(--text)]">{m.pair}</span>
                  <span className={m.side === "BUY" ? "text-accent" : "text-orange-400"}>{m.side}</span>
                  <span className="text-[var(--text)]">{m.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="max-w-[1300px] mx-auto px-5 md:px-8 pb-16">
        <div className="grid sm:grid-cols-3 border border-white/[0.07] rounded-2xl overflow-hidden">
          {stats.map((s, i) => (
            <div key={i} className={`p-6 ${i < 2 ? "sm:border-r border-white/[0.07]" : ""} ${i > 0 ? "border-t sm:border-t-0 border-white/[0.07]" : ""}`}>
              <div className="label text-[var(--muted)] mb-3">{s.label}</div>
              <CountUp value={s.value} className="display text-accent text-3xl mb-2 block" />
              <div className="text-[12px] text-[var(--muted)] leading-snug">{s.note}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[11px] text-[var(--muted)]/70 max-w-3xl">
          Snapshot of the public Myfxbook account, 16/09/2026. Not live data. Past performance is not indicative of future results.
        </p>
      </div>
    </section>
  );
}
